<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn color="primary" x-small text :disabled="submittingForm" @click="$router.back()">
            <v-icon left> fa fa-angle-left </v-icon>
            <span class="sr-only">Voltar</span>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Documento
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="6">
            <e-label>Etapa</e-label>
            <ValidationProvider name="Etapa" rules="required" v-slot="{ errors }">
              <v-select
                v-model="form.etapa_id"
                :items="etapasList"
                :item-text="getEtapaTexto"
                item-value="id"
                label="Selecione uma etapa"
                solo
                dense
                :error-messages="errors"
                no-data-text="Nenhuma etapa 'entrada' no circuito atual"
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="showSeriesField" cols="6">
            <e-label>Series</e-label>
            <ValidationProvider name="Series" rules="required" v-slot="{ errors }">
              <e-label>Séries</e-label>
              <e-autocomplete
                v-model="form.series"
                :items="filteredSeries"
                :error-messages="errors"
                multiple
                item-value="id"
                item-text="descricao"
                label="Selecione as séries"
                solo
                dense
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="showDisciplinaField" cols="12">
            <e-label>Disciplina</e-label>
            <ValidationProvider name="Disciplina" rules="required" v-slot="{ errors }">
              <e-autocomplete
                v-model="form.disciplina_id"
                :items="filteredDisciplinas"
                :error-messages="errors"
                item-value="id"
                item-text="descricao"
                label="Selecione uma disciplina"
                solo
                dense
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="showPdfField" cols="12">
            <e-label>Arquivo PDF</e-label>
            <ValidationProvider name="Arquivo PDF" :rules="pdfValidationRules" v-slot="{ errors }">
              <v-file-input
                v-model="form.pdf"
                accept=".pdf"
                label="Selecione o PDF (Max: 4 MB)"
                outlined
                dense
                :error-messages="errors"
                @change="validatePdfSize"
              />
            </ValidationProvider>
            <div v-if="showCurrentFile">
              Arquivo atual:
              <v-btn x-small class="ml-2" color="red lighten-5" @click="baixarPdf">
                <span class="mr-2">{{ form.filename }}</span>
                <v-icon color="red lighten-1" small> fa fa-file-pdf </v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-btn
              color="primary"
              type="submit"
              :disabled="submittingForm"
              :loading="submittingForm"
            >
              Enviar Dados
            </v-btn>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import ELabel from "@/components/ELabel.vue";

const PERMITE_PLANO = 1;
const PDF_MAX_SIZE = 4 * 1024 * 1024;

export default {
  name: "DocumentoForm",

  components: {
    ELabel,
  },

  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    documentoId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      submittingForm: false,
      form: {
        pdf: null,
        disciplina_id: null,
        etapa_id: null,
        series: [],
        filename: null,
      },
      seriesList: [],
      etapasList: [],
    };
  },

  computed: {
    showSeriesField() {
      return !!this.form.etapa_id || this.editing;
    },

    showDisciplinaField() {
      return !!this.form.series.length || this.editing;
    },

    showPdfField() {
      return !!this.form.disciplina_id || this.editing;
    },

    showCurrentFile() {
      return this.editing && this.form.filename && !this.form.pdf;
    },

    pdfValidationRules() {
      const isUpdatingFile = this.form.pdf instanceof File;
      const needsFile = !this.editing || isUpdatingFile;
      return needsFile ? "required" : "";
    },

    seriesComPlanoPermitido() {
      return this.seriesList.filter((serie) =>
        serie.disciplinas?.some((disciplina) => disciplina.pivot?.permite_plano === PERMITE_PLANO)
      );
    },

    filteredSeries() {
      return this.seriesComPlanoPermitido;
    },

    filteredDisciplinas() {
      let seriesSelecionadas = [];

      if (this.form.series.length) {
        seriesSelecionadas = this.seriesList.filter((serie) => this.form.series.includes(serie.id));
      } else if (this.editing) {
        seriesSelecionadas = this.seriesComPlanoPermitido;
      }

      if (!seriesSelecionadas.length) {
        return [];
      }

      return this.getDisciplinasEmComum(seriesSelecionadas);
    },
  },

  watch: {
    filteredSeries(newList) {
      this.form.series = this.form.series.filter((id) => newList.some((serie) => serie.id === id));
    },

    filteredDisciplinas(newList) {
      if (
        this.form.disciplina_id &&
        !newList.some((disciplina) => disciplina.id === this.form.disciplina_id)
      ) {
        this.form.disciplina_id = null;
      }
    },
  },

  async mounted() {
    await this.loadData();
    if (this.editing) {
      await this.loadDocumento();
    }
  },

  methods: {
    async baixarPdf() {
      this.$loaderService.open("Baixando...");

      try {
        await this.$services.documentosPlanosService.baixarPdf(parseInt(this.documentoId, 10));
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    buildFormData() {
      const formData = new FormData();
      const isUpdatingFile = this.form.pdf instanceof File;

      if (isUpdatingFile) {
        formData.append("file", this.form.pdf);
      }

      formData.append("disciplina_id", this.form.disciplina_id);
      formData.append("etapa_id", this.form.etapa_id);
      this.form.series.forEach((id) => formData.append("series[]", id));

      return { formData, isUpdatingFile };
    },

    getDisciplinasEmComum(series) {
      const [primeira, ...restantes] = series;

      const disciplinasMap = new Map(
        this.getDisciplinasPermitidas(primeira).map((disciplina) => [disciplina.id, disciplina])
      );

      restantes.forEach((serie) => {
        const idsNaSerie = new Set(
          this.getDisciplinasPermitidas(serie).map((disciplina) => disciplina.id)
        );

        Array.from(disciplinasMap.keys()).forEach((id) => {
          if (!idsNaSerie.has(id)) {
            disciplinasMap.delete(id);
          }
        });
      });

      return Array.from(disciplinasMap.values());
    },

    getDisciplinasPermitidas(serie) {
      return (
        serie.disciplinas?.filter(
          (disciplina) => disciplina.pivot?.permite_plano === PERMITE_PLANO
        ) ?? []
      );
    },

    getEtapaTexto(etapa) {
      return `${etapa.descricao} - ${etapa.circuito.descricao}`;
    },

    async loadData() {
      [this.seriesList, this.etapasList] = await Promise.all([
        this.$services.seriesService.syncAll(),
        this.$services.etapasService.getEtapasEntrada(),
      ]);
    },

    async loadDocumento() {
      const documento = await this.$services.documentosPlanosService.buscarPorId(
        parseInt(this.documentoId, 10)
      );

      if (!documento) {
        return;
      }

      this.form = {
        disciplina_id: documento.disciplina_id ?? null,
        etapa_id: documento.etapa_id ?? null,
        series: Array.isArray(documento.series) ? documento.series.map((serie) => serie.id) : [],
        pdf: null,
        filename: documento.filename ?? null,
      };
    },

    async submitForm() {
      this.submittingForm = true;

      try {
        const { formData, isUpdatingFile } = this.buildFormData();

        if (this.form.pdf instanceof File && this.form.pdf.size > PDF_MAX_SIZE) {
          this.$toast.danger("O limite permitido para inserir o PDF é de até 4 MB");
          return;
        }

        if (!this.editing || isUpdatingFile) {
          if (!this.form.pdf) {
            this.$toast.danger("Selecione um arquivo PDF.");
            return;
          }
        }

        if (this.editing) {
          await this.$services.documentosPlanosService.atualizar(this.documentoId, formData);
          this.$toast.success("Documento atualizado com sucesso");
        } else {
          await this.$services.documentosPlanosService.salvar(formData);
          this.$toast.success("Documento criado com sucesso");
        }

        this.$router.push({ name: "gerenciarPlanos" });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.submittingForm = false;
      }
    },

    validatePdfSize(file) {
      if (!file) return;

      if (file.size > PDF_MAX_SIZE) {
        this.$toast.danger("O limite permitido para inserir o PDF é de até 4 MB");
      }
    },
  },
};
</script>

<style scoped></style>
